import { useMemo } from "react";
import { usePermissions } from "../../../context/PermissionContext";
import { getRole } from "../../../common/function";

export interface ExpenseAccess {
  canCreate: boolean;
  canView: boolean;
  canApprove: boolean;
  canReimburse: boolean;
  canAdmin: boolean;
}

/**
 * Defensive permission hook for the expense module.
 *
 * V033 ships proper RBAC permissions (`expense.create`, `expense.view`,
 * `expense.approve`, `expense.reimburse`, `expense.admin`). During the
 * migration window before V033 reaches every environment, fall back to
 * role-based checks so the UI keeps working.
 *
 * Per design doc §8.4.
 */
export function useExpenseAccess(): ExpenseAccess {
  const { hasPermission } = usePermissions();
  const role = getRole() ?? "";
  const isAdminRole = ["Admin", "Super Admin"].includes(role);

  return useMemo(
    () => ({
      canCreate: hasPermission("expense.create") || isAdminRole,
      canView: hasPermission("expense.view") || isAdminRole,
      canApprove: hasPermission("expense.approve") || isAdminRole,
      canReimburse: hasPermission("expense.reimburse") || isAdminRole,
      canAdmin: hasPermission("expense.admin") || isAdminRole,
    }),
    [hasPermission, isAdminRole]
  );
}
